import InteractionSplit from "../interaction/InteractionSplit";
import InteractionVisibility from "../interaction/InteractionVisibility";
import InteractionClipping from "../interaction/InteractionClipping";
import AnalysisVisibility from "../analysis/AnalysisVisibility";
import {
  DitapMask,
  InteractionMove,
  InteractionRotate,
  InteractionScale,
} from "../../..";
import Utils from "./Utils";

/**
 * 분석 도구 모음 위젯. DitapViewer 내부적으로 생성되어 사용하는 위젯.</br>
 * A widget that collects analysis tools. The widget used internally by DitapViewer.
 *
 * @alias AnalysisToolPicker
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {object[]} analysisObjArray 표시 할 분석 도구 배열.</br> Array containing analysis tools to display.
 *
 * @example
 * // 1. DitapViewer 생성 시 AnalysisToolPicker 생성 가능.
 * // analysisTools 사용할 측정 도구 작성. 작성된 분석 도구들을 담은 측정 도구 모음 위젯 생성.
 * // 활성화 - 전체 활성화(analysisTools: true) 또는 부분 활성화(활성화 시킬 기능만 true)
 * // 비활성화 - false 또는 전달 안함
 * const viewer = new Ditap.DitapViewer("container", {
 *    analysisTools: {
 *        visibility: true,
 *        clipping: true,
 *        split: true,
 *    }
 * });
 *
 * // 2. 단독으로 선언하여 활성화
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 * // 사용할 분석 도구 모음 초기화
 * const analysisObjArray = [
 *    new Ditap.InteractionVisibility(viewer),
 *    new Ditap.InteractionClipping(viewer),
 *    new Ditap.InteractionSplit(viewer),
 * ];
 *
 * // 편집 도구 모음 생성
 * const analysisToolPicker =  new Ditap.AnalysisToolPicker(viewer, analysisObjArray);
 */
class AnalysisToolPicker {
  constructor(viewer, analysisObjArray) {
    const container = viewer.toolPickerContainer;
    const mobileContainer = viewer.mobileBtnContainer;
    const that = this;

    this.isMobile = Utils.isBodyWidthLessThan600();

    if (analysisObjArray) {
      // analysisObjArray 갯수에 따라 UI Class 설정

      const li = document.createElement("li");
      const button = document.createElement("button");
      const p = document.createElement("p");
      const p2 = document.createElement("p");
      button.classList.add("ditap-analysis-btn");
      p.classList.add("ditap-function-btn-img");
      p.classList.add("js-ditap-function-btn-img");
      p2.classList.add("ditap-btn-tooltip");
      p2.textContent = "분석";
      button.appendChild(p);
      button.appendChild(p2);
      li.appendChild(button);

      if (this.isMobile) {
        button.classList.add("ditap-mobile-btn");
        li.classList.add("ditap-func-btn-item");
        // mobileContainer.append(li);
        mobileContainer.querySelector(".js-position-analysis").appendChild(li);
      } else {
        button.classList.add("ditap-toolbar-btn");
        li.classList.add("ditap-toolbar-right-item");
        container.appendChild(li);
      }

      const toolPopup = document.createElement("div");
      toolPopup.classList.add("mobile-tool-popup");

      const header = document.createElement("div");
      header.classList.add("tool-popup-header");

      const title = document.createElement("h2");
      title.classList.add("ditap-popup-title", "ditap-mobile-title");
      title.innerText = "분석";

      const btnWrap = document.createElement("div");
      btnWrap.classList.add("ditap-popup-btn-wrap");

      const closeBtn = document.createElement("button");
      closeBtn.classList.add("ditap-popup-close-btn");
      btnWrap.appendChild(closeBtn);
      header.appendChild(title);
      header.appendChild(closeBtn);

      toolPopup.appendChild(header);
      viewer.container.querySelector(".cesium-viewer").append(toolPopup);

      const funcList = document.createElement("div");
      funcList.classList.add("tool-popup-content");

      const ul = document.createElement("ul");

      for (const obj of analysisObjArray) {
        const li2 = document.createElement("li");
        const button = document.createElement("button");
        const p1 = document.createElement("p");
        const p2 = document.createElement("p");

        const toolPicker = this.createToolPicker(viewer, obj);
        // 선택 버튼 엘리먼트
        obj.buttonElement = p1;

        li2.classList.add("ditap-function-card-list");
        button.classList.add("ditap-function-btn");
        button.classList.add(`ditap-${toolPicker.type}-btn`);
        p1.classList.add("ditap-function-btn-img");
        p2.classList.add("ditap-function-btn-title");
        p2.textContent = toolPicker.text;
        button.appendChild(p1);
        button.appendChild(p2);
        li2.appendChild(button);
        ul.append(li2);

        // 기능별 클릭 이벤트
        li2.addEventListener(
          "click",
          (this.toolPickerEvent = (e) => {
            that.close();
            toolPicker.event(e);
          })
        );

        if (this.isMobile) {
          funcList.appendChild(ul);

          ul.classList.add("tool-popup-list");
        } else {
          li.appendChild(ul);
          ul.classList.add(
            "ditap-card",
            "ditap-function-card",
            "js-ditap-function-card",
            "hide"
          );
        }
        toolPopup.appendChild(funcList);
      }

      closeBtn.addEventListener(
        "click",
        (this.closeEvent = (e) => {
          that.close();
        })
      );

      let toolbarClass;
      let count = analysisObjArray.length;

      // 초기화 버튼 추가
      for (const obj of analysisObjArray) {
        if (obj instanceof InteractionVisibility) {
          count++;

          const li2 = document.createElement("li");
          const button = document.createElement("button");
          const p1 = document.createElement("p");
          const p2 = document.createElement("p");

          li2.classList.add("ditap-function-card-list");
          button.classList.add("ditap-function-btn");
          button.classList.add(`ditap-remove-btn`);
          p1.classList.add("ditap-function-btn-img");
          p2.classList.add("ditap-function-btn-title");
          p2.textContent = "초기화";
          button.appendChild(p1);
          button.appendChild(p2);
          li2.appendChild(button);
          ul.append(li2);

          li2.addEventListener(
            "click",
            (this.resetBtnEvent = () => {
              viewer.removeAllAnalysis();
              const interactionSplitElements = analysisObjArray.filter(
                (element) => element instanceof InteractionSplit
              );
              interactionSplitElements[0]?.off();
              that.close();
            })
          );
        }
      }

      if (count === 1) {
        toolbarClass = "width-one";
      } else if (count === 2) {
        toolbarClass = "width-two";
      } else if (count >= 3) {
        toolbarClass = "width-three";
      }
      ul.classList.add(toolbarClass);

      button.addEventListener(
        "click",
        (this.analysisButtonEvent = () => {
          const callback = () => {
            viewer.clickToolbar(that);
          };
          viewer.offAllInterations(callback);
        })
      );

      this._toolPicker = li;
      this._toolList = ul;
      this._imgButton = p;
      this._mobilePopup = toolPopup;
      this._analysisButton = button;
      this._mobileToolList = funcList;
      this.mask = new DitapMask();
      this.createResizeEvent(viewer);
    }
  }

  /*
   * 분석 도구 목록 표시하기 위한 요소.</br>
   * Display elements for the list of analysis tools.
   * @type {Element}
   */
  set toolPicker(toolPicker) {
    this._toolPicker = toolPicker;
  }
  get toolPicker() {
    return this._toolPicker;
  }

  /*
   * 분석 도구 목록 표시하기 위한 요소.</br>
   * Display elements for the list of analysis tools.
   * @type {Element}
   */
  set toolList(toolList) {
    this._toolList = toolList;
  }
  get toolList() {
    return this._toolList;
  }

  /*
   * 분석 도구 비활성화/활성화 버튼 요소.</br>
   * Disable/enable button element for analysis tools.
   * @type {Element}
   */
  set imgButton(imgButton) {
    this._imgButton = imgButton;
  }
  get imgButton() {
    return this._imgButton;
  }

  /*
   * 분석 도구모음 생성
   * @param {*} viewer
   * @param {*} obj
   * @returns
   */
  createToolPicker(viewer, obj) {
    let text;
    let type;
    let onInteraction;

    if (obj instanceof InteractionVisibility) {
      text = "가시성";
      type = "visibility";
      onInteraction = () => {
        const analysisVisibility = new AnalysisVisibility(viewer);
        viewer.ditapStorage.analysisVisibility[
          analysisVisibility.id
        ] = analysisVisibility;

        obj.analysisVisibility = analysisVisibility;
        obj.on();
      };
    } else if (obj instanceof InteractionClipping) {
      text = "절단면";
      type = "clipping";
      onInteraction = () => {
        obj.on();
      };
    } else if (obj instanceof InteractionSplit) {
      text = "분할";
      type = "split";
      onInteraction = () => {
        obj.toggle();
      };
    }

    const event = () => {
      if (!obj.isOn) {
        if (
          (viewer.currentEvent instanceof InteractionScale ||
            viewer.currentEvent instanceof InteractionRotate ||
            viewer.currentEvent instanceof InteractionMove) &&
          viewer.currentEvent.selectObject
        ) {
          // 기존에 실행된 인터렉션 있을 경우 종료
          viewer.offAllInterations(onInteraction);
        } else {
          viewer.offAllInterations();
          onInteraction();
        }
      } else if (obj instanceof InteractionSplit) {
        obj.toggle();
      } else {
        obj.off();
      }
    };

    return {
      text: text,
      type: type,
      event: event,
    };
  }

  /**
   * 분석 도구 모음 활성화.</br>
   * Activate the collection of analysis tools.
   */
  open() {
    this.mask.openMobile();
    this.toolPicker.classList.add("js-toolbar-on");
    this.imgButton.classList.add("on");
    if (this.isMobile) {
      this._mobilePopup.style.display = "block";
    } else {
      this._toolList.classList.remove("hide");
    }
  }

  /**
   * 분석 도구 모음 비활성화.</br>
   * Deactivate the collection of analysis tools.
   */
  close() {
    this.mask.closeMobile();
    this.toolPicker.classList.remove("js-toolbar-on");
    this.imgButton.classList.remove("on");
    if (this.isMobile) {
      this._mobilePopup.style.display = "none";
    } else {
      this._toolList.classList.add("hide");
    }
  }

  /**
   * 인스턴스 제거. </br>
   * Destroys the instance.
   */
  destroy() {
    this.removeEvent();
    this.toolPicker = undefined;
    this.toolList = undefined;
    this.imgButton = undefined;
    this._mobilePopup = undefined;
    this._analysisButton = undefined;
    this._mobileToolList = undefined;
    this.mask = undefined;
    this.isMobile = false;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent(viewer) {
    const container = viewer.toolPickerContainer;
    const mobileContainer = viewer.mobileBtnContainer;
    const that = this;

    const resizeOriginal = () => {
      // 윈도우의 현재 너비를 확인
      const currentIsMobile = Utils.isBodyWidthLessThan600();

      if (this.isMobile !== currentIsMobile) {
        if (currentIsMobile) {
          this.isMobile = true;
          this._analysisButton.classList.add("ditap-mobile-btn");
          this._analysisButton.classList.remove("ditap-toolbar-btn");

          this._toolPicker.classList.add("ditap-func-btn-item");
          this._toolPicker.classList.remove("ditap-toolbar-right-item");
          // mobileContainer.appendChild(this._toolPicker);
          mobileContainer
            .querySelector(".js-position-analysis")
            .appendChild(this._toolPicker);

          this._toolList.classList.add("tool-popup-list");
          this._toolList.classList.remove(
            "ditap-card",
            "ditap-function-card",
            "js-ditap-function-card",
            "hide"
          );
          this._mobileToolList.appendChild(this._toolList);

          if (that._imgButton.classList.contains("on")) {
            this._mobilePopup.style.display = "block";
          }
        } else {
          this.isMobile = false;
          this._analysisButton.style.display = "flex";
          this._analysisButton.classList.remove("ditap-mobile-btn");
          this._analysisButton.classList.add("ditap-toolbar-btn");

          this._toolPicker.classList.add("ditap-toolbar-right-item");
          this._toolPicker.classList.remove("ditap-func-btn-item");
          container.appendChild(this._toolPicker);

          this._mobilePopup.style.display = "none";
          this._toolPicker.appendChild(this._toolList);
          this._toolList.classList.add(
            "ditap-card",
            "ditap-function-card",
            "js-ditap-function-card"
          );
          if (!that._imgButton.classList.contains("on")) {
            this._toolList.classList.add("hide");
            this._toolPicker.classList.remove("js-toolbar-on");
          }
          this._toolList.classList.remove("tool-popup-list");
        }
      }
    };
    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };
    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 이벤트 제거.<br>
   * Remove the event registered on the window object.
   */
  removeEvent() {
    if (this.resizeEvent) {
      window.removeEventListener("resize", this.resizeEvent);
    }
    if (this.closeEvent) {
      window.removeEventListener("click", this.closeEvent);
    }
    if (this.toolPickerEvent) {
      window.removeEventListener("click", this.toolPickerEvent);
    }
    if (this.resetBtnEvent) {
      window.removeEventListener("click", this.resetBtnEvent);
    }
    if (this.analysisButtonEvent) {
      window.removeEventListener("click", this.analysisButtonEvent);
    }

    this.toolPickerEvent = undefined;
    this.resetBtnEvent = undefined;
    this.analysisButtonEvent = undefined;
    this.resizeEvent = undefined;
    this.closeEvent = undefined;
  }
}
export default AnalysisToolPicker;
